// Callout: a short message in a box. neutral is outlined (a notice, an error the API worded);
// info, success, warning and danger sit on their tone's faded fill in its ink, or with `outline`
// in a border of the tone's hue on the page. `icon` leads, `title` is a bold lead-in. Give it role="alert" / "status" / "note" where it is one. Ported from
// the Humint Assistant's components/ui/callout.tsx.
import type { ComponentProps, ReactNode } from 'react';
import { cn } from './cn';

export type CalloutTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const TONE: Record<CalloutTone, string> = {
  neutral: 'border border-hairline-strong',
  info: 'bg-signal-faded text-signal-ink',
  success: 'bg-success-faded text-success-ink',
  warning: 'bg-warning-faded text-warning-ink',
  danger: 'bg-danger-faded text-danger-ink',
};

const OUTLINE: Record<CalloutTone, string> = {
  neutral: 'border border-hairline-strong',
  info: 'border border-signal text-signal-ink',
  success: 'border border-success text-success-ink',
  warning: 'border border-warning text-warning-ink',
  danger: 'border border-danger text-danger-ink',
};

export interface CalloutProps extends Omit<ComponentProps<'div'>, 'title' | 'ref'> {
  tone?: CalloutTone;
  outline?: boolean;
  icon?: ReactNode;
  title?: ReactNode;
  as?: 'div' | 'p';
}

export function Callout({ tone = 'neutral', outline, icon, title, as: As = 'div', className, children, ...props }: CalloutProps) {
  return (
    <As className={cn('m-0 flex items-start gap-2 rounded px-3 py-[9px]', (outline ? OUTLINE : TONE)[tone], className)} {...props}>
      {icon && <span className="mt-0.5 flex flex-none">{icon}</span>}
      {title || icon ? (
        <span className="min-w-0">
          {title && <strong className="mr-1.5 text-inherit">{title}</strong>}
          {children}
        </span>
      ) : (
        children
      )}
    </As>
  );
}
